import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { useContext } from "react";
import z from "zod";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { lifecycleQueryOptions } from "@/services/lifecycle/lifecycle.options";
import { LifecycleDrawerContext } from "@/utils/lifecycle-detail/lifecycle-drawer-context";
import type { OrgLifecycleFullPageTab } from "./OrgLifecycleFullPage";
import { OrgLifecycleFullPage } from "./OrgLifecycleFullPage";

const SearchSchema = z.object({
	tab: z
		.enum(["details", "statuses", "transitions", "rules", "activity"])
		.optional()
		.default("details"),
	mode: z.enum(["view", "edit"]).optional().default("view"),
});

const FULL_PARENT =
	"/org-admin/$orgId/configuration/lifecycles/$lifecycleId/full" as const;

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId/full",
)({
	component: OrgLifecycleFullView,
	validateSearch: (search) => SearchSchema.parse(search),
});

function OrgLifecycleFullView() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId/full",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId/full",
	});

	const detailQuery = useQuery(
		lifecycleQueryOptions.statusLifecycleDetail(
			params.lifecycleId,
			params.orgId,
		),
	);
	const lifecycle = detailQuery.data ?? null;
	const drawerCtx = useContext(LifecycleDrawerContext);

	const isEditMode = search.mode === "edit";

	const handleTabChange = (tab: OrgLifecycleFullPageTab) => {
		void navigate({
			to: FULL_PARENT,
			params: { orgId: params.orgId, lifecycleId: params.lifecycleId },
			search: { tab, mode: "view" },
		});
	};

	const handleBackToList = () => {
		void navigate({
			to: "/org-admin/$orgId/configuration/lifecycles",
			params: { orgId: params.orgId },
		});
	};

	if (!lifecycle && !detailQuery.isLoading && !detailQuery.isError) {
		return null;
	}

	if (!lifecycle) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-sm text-gray-500">
					{detailQuery.isLoading
						? t("common.actions.loadingEllipsis")
						: t("admin.lifecycles.drawer.errors.notFound")}
				</div>
			</div>
		);
	}

	if (!drawerCtx) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-sm text-gray-500">
					{t("common.actions.loadingEllipsis")}
				</div>
			</div>
		);
	}

	const fullPageContext = {
		...drawerCtx,
		mode: search.mode as "view" | "edit",
		setMode: (mode: "view" | "edit") => {
			void navigate({
				to: FULL_PARENT,
				params: { orgId: params.orgId, lifecycleId: params.lifecycleId },
				search: { tab: search.tab, mode },
			});
		},
		cancelDraft: () => {
			drawerCtx.setDraftName(drawerCtx.lifecycle.name ?? "");
			drawerCtx.setDraftDescription(drawerCtx.lifecycle.description ?? "");
			void navigate({
				to: FULL_PARENT,
				params: { orgId: params.orgId, lifecycleId: params.lifecycleId },
				search: { tab: search.tab, mode: "view" },
			});
		},
	};

	return (
		<LifecycleDrawerContext.Provider value={fullPageContext}>
			<OrgLifecycleFullPage
				lifecycle={lifecycle}
				activeTab={search.tab}
				onTabChange={handleTabChange}
				onBackToList={handleBackToList}
				isLoading={detailQuery.isLoading}
				isError={detailQuery.isError}
				isEditMode={isEditMode}
			/>
		</LifecycleDrawerContext.Provider>
	);
}
